<template>
  <section class="settings-card">
    <div class="card-header">
      <h2 class="card-title">神客考试认证</h2>
    </div>
    <p class="card-desc" style="margin-bottom: 20px;">
      开启后，代理可通过"神客名单"向神客发送唯一考试邀请，神客完成考试并达到通过线后方可被分配任务。
    </p>

    <el-form :model="form" ref="form" label-position="top" class="info-form">
      <el-form-item label="开启考试要求">
        <el-switch
          v-model="form.requireExam"
          :active-value="1"
          :inactive-value="0"
          active-color="#1476ff"
          @change="saveSettings"
        />
        <span class="hint-text">{{ form.requireExam ? '已启用 - 未通过考试的神客分配时将触发提醒' : '未启用 - 神客无需考试即可分配任务' }}</span>
      </el-form-item>

      <template v-if="form.requireExam">
        <div class="form-row">
          <el-form-item label="考试题目" class="form-half" prop="examFormKey">
            <el-select
              v-model="form.examFormKey"
              placeholder="请选择一个考试表单"
              filterable
              clearable
              style="width: 100%;"
              :loading="loadingForms"
              @change="saveSettings"
            >
              <el-option
                v-for="f in examForms"
                :key="f.formKey"
                :label="stripHtml(f.name)"
                :value="f.formKey"
              >
                <span>{{ stripHtml(f.name) }}</span>
                <span style="float: right; color: #8492a6; font-size: 12px;">
                  {{ f.status === 2 ? '已发布' : '草稿' }}
                </span>
              </el-option>
              <template v-if="!loadingForms && examForms.length === 0" slot="empty">
                <div style="padding: 20px; text-align: center; color: #94a3b8;">
                  当前项目下暂无考试类型(form_type=3)的问卷，请先创建
                </div>
              </template>
            </el-select>
          </el-form-item>

          <el-form-item label="通过线(分)" class="form-half">
            <el-input-number
              v-model="form.examPassScore"
              :min="1"
              :max="100"
              :step="5"
              controls-position="right"
              style="width: 100%;"
              @change="saveSettings"
            />
          </el-form-item>
        </div>

        <el-form-item label="允许考试次数（0 = 不限）">
          <el-input-number
            v-model="form.examMaxAttempts"
            :min="0"
            :max="99"
            :step="1"
            controls-position="right"
            style="width: 180px;"
            @change="saveSettings"
          />
          <span class="hint-text">
            {{ form.examMaxAttempts > 0
              ? `神客在本项目下最多可提交 ${form.examMaxAttempts} 次考试，达到上限后代理无法再发起邀请`
              : '未限制考试次数，神客可被多次发起考试邀请' }}
          </span>
        </el-form-item>

        <el-form-item label="允许代理发送考试短信">
          <el-switch
            v-model="form.examSmsEnabled"
            :active-value="1"
            :inactive-value="0"
            active-color="#1476ff"
            @change="saveSettings"
          />
          <span class="hint-text">{{ form.examSmsEnabled ? '已开启 - 代理可通过短信向神客发送考试链接' : '未开启 - 代理只能复制链接手动发送' }}</span>
        </el-form-item>

        <el-form-item label="启用独立廉政承诺确认页">
          <el-switch
            v-model="form.examAgreementPopup"
            :active-value="1"
            :inactive-value="0"
            active-color="#1476ff"
            @change="saveSettings"
          />
          <span class="hint-text">
            {{ form.examAgreementPopup
              ? '已开启 - 考试前先弹出代理盖章的廉政协议 PDF，神客确认后方可答题'
              : '未开启 - 承诺由考试表单内的"廉政协议"题目完成，提交时按该题是否勾选回写合规状态' }}
          </span>
        </el-form-item>

        <div v-if="form.examAgreementPopup" class="form-tip">
          <i class="el-icon-info"></i>
          启用后神客打开考试链接会先跳转到承诺页 <code>/s/agreement/:token</code>，展示代理上传的盖章 PDF，勾选确认后才能进入答题。导出考试结果时可下载带确认记录的承诺书 PDF。
        </div>

        <div v-if="form.examFormKey" class="form-tip">
          <i class="el-icon-info"></i>
          考试链接：<code>/s/{{ form.examFormKey }}?inviteToken=xxx</code>
          （代理发送邀请时会携带 token{{ form.examAgreementPopup ? '，启用承诺页时链接为 /s/agreement/:token' : '' }}）
        </div>
      </template>

      <div class="form-actions">
        <el-button
          type="primary"
          class="save-btn"
          :loading="saving"
          @click="saveSettings"
        >保存考试设置</el-button>
      </div>
    </el-form>
  </section>
</template>

<script>
import { getProjectDetail, updateProject, getProjectExamForms } from 'index/api/project/manager'

export default {
  name: 'ExamSection',
  props: {
    projectId: { type: [String, Number], required: true }
  },
  data() {
    return {
      saving: false,
      loadingForms: false,
      // 保存项目原始字段，避免 saveSettings 覆盖其他字段
      projectRaw: {},
      form: {
        requireExam: 0,
        examFormKey: '',
        examPassScore: 60,
        examSmsEnabled: 0,
        examAgreementPopup: 0,
        examMaxAttempts: 0
      },
      examForms: []
    }
  },
  mounted() {
    this.loadProject()
    this.loadExamForms()
  },
  methods: {
    async loadProject() {
      try {
        const res = await getProjectDetail(this.projectId)
        const data = res?.data?.data || res?.data || res
        if (data) this.applyData(data)
      } catch (e) {
        console.error('加载项目详情失败', e)
        const data = e?.data?.data || e?.data
        if (data) this.applyData(data)
      }
    },

    applyData(data) {
      // 缓存现有字段，保存时一并回传避免 Mapster Adapt 把其他字段覆盖为 null/默认值
      this.projectRaw = {
        name: data.name || data.Name,
        code: data.code || data.Code,
        customerName: data.customerName || data.CustomerName || data.customer_name,
        description: data.description || data.Description || data.remark,
        status: data.status || data.Status || 1,
        startTime: data.startTime || data.StartTime || data.start_time,
        endTime: data.endTime || data.EndTime || data.end_time,
        targetQuota: data.targetQuota || data.TargetQuota || data.target_quota
      }
      this.form.requireExam = (data.requireExam ?? data.RequireExam ?? data.require_exam ?? 0)
      this.form.examFormKey = data.examFormKey || data.ExamFormKey || data.exam_form_key || ''
      this.form.examPassScore = data.examPassScore ?? data.ExamPassScore ?? data.exam_pass_score ?? 60
      this.form.examSmsEnabled = data.examSmsEnabled ?? data.ExamSmsEnabled ?? data.exam_sms_enabled ?? 0
      this.form.examAgreementPopup = data.examAgreementPopup ?? data.ExamAgreementPopup ?? data.exam_agreement_popup ?? 0
      this.form.examMaxAttempts = data.examMaxAttempts ?? data.ExamMaxAttempts ?? data.exam_max_attempts ?? 0
    },

    async loadExamForms() {
      this.loadingForms = true
      try {
        const res = await getProjectExamForms(this.projectId)
        const data = res?.data?.data || res?.data || res
        this.examForms = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('加载考试表单列表失败', e)
        this.examForms = []
      } finally {
        this.loadingForms = false
      }
    },

    // 剥离 HTML 标签（问卷名在富文本编辑器里录入时会带 <p> 等标签）
    stripHtml(html) {
      if (!html) return ''
      // 同时处理 &nbsp; &amp; &lt; &gt; 等常见实体
      const tmp = document.createElement('div')
      tmp.innerHTML = String(html)
      return (tmp.textContent || tmp.innerText || '').trim()
    },

    async saveSettings() {
      // 启用考试但未选表单时给出提示
      if (this.form.requireExam === 1 && !this.form.examFormKey) {
        this.$message.warning('请选择一个考试表单')
        return
      }
      this.saving = true
      try {
        await updateProject({
          id: this.projectId,
          // 合并原有字段避免被 Mapster Adapt 覆盖为 null/默认值
          name: this.projectRaw.name,
          code: this.projectRaw.code,
          customerName: this.projectRaw.customerName,
          description: this.projectRaw.description,
          status: this.projectRaw.status,
          startTime: this.projectRaw.startTime,
          endTime: this.projectRaw.endTime,
          targetQuota: this.projectRaw.targetQuota,
          // 本组件负责的考试字段
          requireExam: this.form.requireExam,
          examFormKey: this.form.examFormKey || null,
          examPassScore: this.form.examPassScore || 60,
          examSmsEnabled: this.form.examSmsEnabled,
          examAgreementPopup: this.form.examAgreementPopup,
          examMaxAttempts: this.form.examMaxAttempts || 0
        })
        this.$message.success('保存成功')
      } catch (e) {
        this.$message.error(e?.data?.message || e?.message || '保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hint-text {
  margin-left: 12px;
  color: #94a3b8;
  font-size: 12px;
}

.form-tip {
  margin: 8px 0 16px;
  padding: 10px 14px;
  background: #f0f7ff;
  border-radius: 8px;
  font-size: 12px;
  color: #1476ff;

  i { margin-right: 6px; }

  code {
    background: #fff;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    color: #334155;
  }
}
</style>
